import React, { FC, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { DealRoom } from 'models/DealRoom';
import Tag from 'components/ListingPortfolioCard/components/Tag';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ResolveImage from 'components/ResolveImage';
import { getImgUrl } from 'components/ResolveImage/utils';
import utils, {
  getAskingPrice,
  getBuyingPortfolio,
  getSellingPortfolio,
} from 'utils';
import {
  DEAL_STATUS_COLOR,
  DEAL_STATUS_LABEL,
  LISTING_STATUS_COLOR,
  LISTING_STATUS_LABEL,
} from 'data/dealStatus';
import { TextHighlighter } from 'components/TextHighlighter';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { DEAL_ROOM_SEARCH, DealRoomSearch } from 'models/Search';
import { COLOR_VARIANT } from 'models/Colors';

import './DealRoomCard.styles.scss';

type DealRoomCardProps = {
  className?: string;
  dealRoom: DealRoom;
  type: DealRoomSearch;
  searchString?: string;
};

const DealRoomCard: FC<DealRoomCardProps> = ({
  className,
  dealRoom,
  type,
  searchString,
}) => {
  const classes = classNames('deal-room-card', className);

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { t } = useTranslation();

  const {
    _id,
    listingEnquiryId,
    listing,
    location,
    industry,
    stage,
    listingStage,
    portfolioName,
  } = dealRoom;
  const isWithdrawn = listing?.isWithdrawn;

  const navigationUrl = useMemo(() => {
    if (type === DEAL_ROOM_SEARCH.BUYING)
      return getBuyingPortfolio(listingEnquiryId);

    if (type === DEAL_ROOM_SEARCH.MARKETPLACE) {
      if (isSuperAdmin) return getSellingPortfolio(_id);

      return utils.composeBusinessUrl(
        industry,
        listing?.slugName,
        listing?.type,
      );
    }

    return getSellingPortfolio(_id);
  }, [
    _id,
    industry,
    isSuperAdmin,
    listing?.slugName,
    listing?.type,
    listingEnquiryId,
    type,
  ]);

  return (
    <Link className={classes} to={navigationUrl}>
      <ResolveImage src={getImgUrl(listing?.gallery[0]?.url, '100')} />

      <TextHighlighter
        className="deal-room-card__name"
        text={portfolioName}
        highlighter={searchString}
      />

      {isWithdrawn ? (
        <>
          <Tag variant={COLOR_VARIANT.RED_FULL} content={t('withdrawn')} />
          <span />
        </>
      ) : (
        <>
          <Tag
            variant={DEAL_STATUS_COLOR[stage]}
            content={t(DEAL_STATUS_LABEL[stage])}
          />

          <Tag
            variant={LISTING_STATUS_COLOR[listingStage]}
            content={t(LISTING_STATUS_LABEL[listingStage])}
          />
        </>
      )}

      <p>{getAskingPrice(listing)}</p>
      <p>{location}</p>
      <p className="deal-room-card__industry">{industry}</p>
    </Link>
  );
};

export default DealRoomCard;
